import { useCallback } from 'react';
import type React from 'react';
import * as Y from 'yjs';
import { DEFAULT_NODE_COLOR } from '@/features/canvas/utils/constants';
import type { ShapeKind } from '@/features/canvas/engine/nodes/shape/shapes';
import { canvasRoots, type CanvasBinding } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';
import { useYjsSnapshot } from '@/lib/sync/use-yjs-snapshot';

export type FontFamily = string;
export type StrokeStyle = 'solid' | 'dashed' | 'dotted';

export interface FormattingState {
  fontSize: number;
  fontFamily: FontFamily;
  textAlign: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'middle' | 'bottom';
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  textColor: string;
  fillColor: string | 'none';
  fillOpacity: number;
  strokeColor: string;
  strokeOpacity: number;
  strokeStyle: StrokeStyle;
  /** Only populated for shape nodes — undefined for sticky/text. */
  shapeKind?: ShapeKind;
  setFontSize: (v: number) => void;
  setFontFamily: (v: FontFamily) => void;
  setTextAlign: (v: 'left' | 'center' | 'right') => void;
  setVerticalAlign: (v: 'top' | 'middle' | 'bottom') => void;
  toggleBold: () => void;
  toggleItalic: () => void;
  toggleUnderline: () => void;
  toggleStrikethrough: () => void;
  setTextColor: (v: string) => void;
  setFillColor: (v: string | 'none') => void;
  setFillOpacity: (v: number) => void;
  setStrokeColor: (v: string) => void;
  setStrokeOpacity: (v: number) => void;
  setStrokeStyle: (v: StrokeStyle) => void;
  setShapeKind: (v: ShapeKind) => void;
}

function readState(data: Y.Map<unknown>) {
  return {
    fontSize: (data.get('fontSize') as number | undefined) ?? 14,
    fontFamily: ((data.get('fontFamily') as string | undefined) ?? 'sans') as FontFamily,
    textAlign: (data.get('textAlign') as 'left' | 'center' | 'right' | undefined) ?? 'left',
    verticalAlign: (data.get('verticalAlign') as 'top' | 'middle' | 'bottom' | undefined) ?? 'middle',
    bold: (data.get('bold') as boolean | undefined) ?? false,
    italic: (data.get('italic') as boolean | undefined) ?? false,
    underline: (data.get('underline') as boolean | undefined) ?? false,
    strikethrough: (data.get('strikethrough') as boolean | undefined) ?? false,
    textColor: (data.get('textColor') as string | undefined) ?? DEFAULT_NODE_COLOR,
    fillColor: (data.get('fill') as string | 'none' | undefined) ?? 'none',
    fillOpacity: (data.get('fillOpacity') as number | undefined) ?? 1,
    strokeColor: (data.get('stroke') as string | undefined) ?? DEFAULT_NODE_COLOR,
    strokeOpacity: (data.get('strokeOpacity') as number | undefined) ?? 1,
    strokeStyle: (data.get('strokeStyle') as StrokeStyle | undefined) ?? 'solid',
    shapeKind: (data.get('shapeKind') as ShapeKind | undefined),
  };
}

function getDataMap(doc: Y.Doc, nodeId: string, binding: CanvasBinding): Y.Map<unknown> | null {
  const nodes = canvasRoots(doc, binding).nodes;
  const node = nodes.get(nodeId);
  if (!node) return null;
  const data = node.get('data');
  if (!(data instanceof Y.Map)) return null;
  return data;
}

export interface FormattingVarsInput {
  fontSize: number;
  fontFamily?: FontFamily | string;
  textAlign: 'left' | 'center' | 'right';
  verticalAlign: 'top' | 'middle' | 'bottom';
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  textColor: string;
}

// Legacy short keys stored before full font-family strings were used
const LEGACY_FONT_ALIAS: Record<string, string> = {
  sans: 'var(--font-sans)',
  serif: 'var(--font-serif)',
  mono: 'var(--font-mono)',
};

/** Returns a CSS custom property object using the shared --fmt-* namespace.
 *  Apply to the editor container; pair with .formattingText in formatting.module.css. */
export function formattingVars(d: FormattingVarsInput): React.CSSProperties {
  return {
    '--fmt-font-size': `${d.fontSize}px`,
    '--fmt-font-family': d.fontFamily ? (LEGACY_FONT_ALIAS[d.fontFamily] ?? d.fontFamily) : 'var(--font-sans)',
    '--fmt-text-align': d.textAlign,
    '--fmt-vertical-align': d.verticalAlign === 'top' ? 'flex-start' : d.verticalAlign === 'bottom' ? 'flex-end' : 'center',
    '--fmt-font-weight': d.bold ? '700' : '400',
    '--fmt-font-style': d.italic ? 'italic' : 'normal',
    '--fmt-text-decoration': [d.underline && 'underline', d.strikethrough && 'line-through'].filter(Boolean).join(' ') || 'none',
    '--fmt-text-color': d.textColor,
  } as React.CSSProperties;
}

export function useFormattingState(doc: Y.Doc, nodeId: string): FormattingState | null {
  const binding = useCanvasBinding();
  const snapshot = useYjsSnapshot<ReturnType<typeof readState> | null>(
    () => {
      const d = getDataMap(doc, nodeId, binding);
      return d ? readState(d) : null;
    },
    (refresh) => {
      const d = getDataMap(doc, nodeId, binding);
      if (!d) return () => {};
      d.observe(refresh);
      return () => d.unobserve(refresh);
    },
    [doc, nodeId, binding],
  );

  const write = useCallback((key: string, value: unknown) => {
    const d = getDataMap(doc, nodeId, binding);
    if (!d) return;
    doc.transact(() => d.set(key, value), doc.clientID);
  }, [doc, nodeId, binding]);

  const setFontSize = useCallback((v: number) => write('fontSize', v), [write]);
  const setFontFamily = useCallback((v: FontFamily) => write('fontFamily', v), [write]);
  const setTextAlign = useCallback((v: 'left' | 'center' | 'right') => write('textAlign', v), [write]);
  const setVerticalAlign = useCallback((v: 'top' | 'middle' | 'bottom') => write('verticalAlign', v), [write]);
  const toggleBold = useCallback(() => {
    const d = getDataMap(doc, nodeId, binding);
    if (!d) return;
    doc.transact(() => d.set('bold', !(d.get('bold') ?? false)), doc.clientID);
  }, [doc, nodeId, binding]);
  const toggleItalic = useCallback(() => {
    const d = getDataMap(doc, nodeId, binding);
    if (!d) return;
    doc.transact(() => d.set('italic', !(d.get('italic') ?? false)), doc.clientID);
  }, [doc, nodeId, binding]);
  const toggleUnderline = useCallback(() => {
    const d = getDataMap(doc, nodeId, binding);
    if (!d) return;
    doc.transact(() => d.set('underline', !(d.get('underline') ?? false)), doc.clientID);
  }, [doc, nodeId, binding]);
  const toggleStrikethrough = useCallback(() => {
    const d = getDataMap(doc, nodeId, binding);
    if (!d) return;
    doc.transact(() => d.set('strikethrough', !(d.get('strikethrough') ?? false)), doc.clientID);
  }, [doc, nodeId, binding]);
  const setTextColor = useCallback((v: string) => write('textColor', v), [write]);
  const setFillColor = useCallback((v: string | 'none') => write('fill', v), [write]);
  const setFillOpacity = useCallback((v: number) => write('fillOpacity', v), [write]);
  const setStrokeColor = useCallback((v: string) => write('stroke', v), [write]);
  const setStrokeOpacity = useCallback((v: number) => write('strokeOpacity', v), [write]);
  const setStrokeStyle = useCallback((v: StrokeStyle) => write('strokeStyle', v), [write]);
  const setShapeKind = useCallback((v: ShapeKind) => write('shapeKind', v), [write]);

  if (!snapshot) return null;

  return {
    ...snapshot,
    setFontSize,
    setFontFamily,
    setTextAlign,
    setVerticalAlign,
    toggleBold,
    toggleItalic,
    toggleUnderline,
    toggleStrikethrough,
    setTextColor,
    setFillColor,
    setFillOpacity,
    setStrokeColor,
    setStrokeOpacity,
    setStrokeStyle,
    setShapeKind,
  };
}
